import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Copy-to-clipboard with its own confirmation.
 *
 * antd's `<Typography.Text copyable>` did this in the old app; this keeps the
 * affordance (and the tick) without the dependency, and it stays out of the tab
 * order until the row is hovered or focused so a keyboard user walking a table
 * is not stopped at every email address.
 */
export function CopyButton({
  value,
  label,
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      aria-label={label ?? t('common.copy')}
      onClick={async (event) => {
        event.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch {
          // Clipboard access is denied outside a secure context; failing
          // silently is better than an error toast for a convenience action.
        }
      }}
      className={cn(
        'text-muted-foreground hover:text-foreground inline-flex size-6 shrink-0 items-center justify-center rounded transition-colors',
        'focus-visible:ring-ring/50 outline-none focus-visible:ring-2',
        'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
        copied && 'text-success opacity-100',
        className
      )}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}
